import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Container, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { localGet } from '../../../utils/localStorageUtils';
import { getSecuritySettings, saveSecuritySettings } from '../../../utils/securityService';

/**
 * District security settings (Nov 2026): idle sign-out for the portal and the responder app, and
 * the two-step sign-in status. District admins (own district) and DiscloSure admins.
 */
const SecurityPanel = ({ data }) => {
  const theme = useTheme();
  const [user, setUser] = useState(null);
  const [values, setValues] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const district = data?.id;

  useEffect(() => { (async () => setUser(await localGet('user', true)))(); }, []);
  const allowed = user?.permission === 'disclosureAdmin' || (user?.permission === 'districtAdmin' && user?.district === district);

  useEffect(() => {
    if (!allowed || !district) return;
    (async () => {
      const res = await getSecuritySettings(district);
      if (res.ok) setValues(res); else setMsg({ severity: 'error', text: res.message });
    })();
  }, [allowed, district]);

  if (!allowed) return null;
  const border = `1px solid ${theme.palette.custom?.gray || '#ccc'}`;
  const muted = { fontSize: '14px', color: theme.palette.text?.secondary || '#666', lineHeight: 1.5 };

  const save = async () => {
    setBusy(true); setMsg(null);
    const res = await saveSecuritySettings(district, {
      portalIdleMinutes: values.portalIdleMinutes, appIdleHours: values.appIdleHours,
    });
    setBusy(false);
    if (res.ok) { setValues((v) => ({ ...v, ...res })); setMsg({ severity: 'success', text: 'Saved.' }); }
    else setMsg({ severity: 'error', text: res.message });
  };

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', marginLeft: '60px', marginRight: '60px', marginTop: '40px',
                     width: 'auto', paddingLeft: '0px !important', border, borderRadius: '4px' }}>
      <Box sx={{ borderBottom: border }}>
        <Typography sx={{ fontSize: '20px', fontWeight: 700, margin: '20px' }}>Sign-in security</Typography>
      </Box>
      <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Typography sx={muted}>
          Staff are signed out automatically after a period with no activity. The responder app uses a longer limit
          so emergency alerts keep reaching responders' phones.
        </Typography>
        {values && (
          <>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" type="number" label="Portal: sign out after (minutes)"
                         inputProps={{ min: 5, max: 240 }} value={values.portalIdleMinutes}
                         onChange={(e) => setValues({ ...values, portalIdleMinutes: e.target.value })} sx={{ width: 260 }} />
              <TextField size="small" type="number" label="Responder app: sign out after (hours)"
                         inputProps={{ min: 1, max: 72 }} value={values.appIdleHours}
                         onChange={(e) => setValues({ ...values, appIdleHours: e.target.value })} sx={{ width: 280 }} />
              <Button variant="contained" onClick={save} disabled={busy} sx={{ textTransform: 'none', boxShadow: 'none' }}>Save</Button>
            </Box>
            <Typography sx={{ fontSize: '14px' }}>
              <strong>Two-step sign-in:</strong>{' '}
              {values.staffMfa === 'required' ? 'required for all staff.' : 'available to all staff (not yet required).'}
            </Typography>
          </>
        )}
        {msg && <Alert severity={msg.severity}>{msg.text}</Alert>}
      </Box>
    </Container>
  );
};

export default SecurityPanel;
